import React from 'react';
import {
  FormCell,
  Grid,
  ISelectOption,
  ISpruceFieldProps,
  NumberInputStyled,
  PhoneNumberInput,
  Select,
  SpruceField,
  TextInput,
} from 'ui-toolkit';
import { activeThirdPartyContactsFunctionsOptions } from '../../../../types/contacts/activeThirdPartyContactsFunctionsOptions';
import { NumberFormatValues } from 'react-number-format';
import { useListContactFunctionsQuery } from '../../../../API/contacts/ContactFunctionsApi';
import { getContactFunctionValue } from '../../../../utilities/contact/getContactFunctionValue';
import { OrderContactNotesField } from '../subComponents/OrderContactNotesField';
import { StatesLabelValue } from '../../../../ui-toolkit/enums/states';

interface IThirdPartyContactFormProps {
  showAddressFields?: boolean;
  disableEmail?: boolean;
}

export const ThirdPartyContactForm: React.FC<IThirdPartyContactFormProps> = ({ showAddressFields, disableEmail }) => {
  const { data: contactFunctions = [] } = useListContactFunctionsQuery();

  const stateFilterOptions = (option: ISelectOption<string>, value: string) => {
    return option.value.toLowerCase().indexOf((value || '').toLowerCase()) >= 0;
  };

  return (
    <>
      <SpruceField name={'firstName'}>
        {({ field }: ISpruceFieldProps) => <TextInput {...field} label={'First Name'} isRequired />}
      </SpruceField>
      <SpruceField name={'middleName'}>
        {({ field }: ISpruceFieldProps) => <TextInput {...field} label={'Middle Name'} />}
      </SpruceField>
      <SpruceField name={'lastName'}>
        {({ field }: ISpruceFieldProps) => <TextInput {...field} label={'Last Name'} isRequired />}
      </SpruceField>
      <SpruceField name={'functions'}>
        {({ field }: ISpruceFieldProps) => (
          <Select
            {...field}
            onChange={(values: ISelectOption[]) => field.onChange(values.map((value) => value.value))}
            value={getContactFunctionValue(field.value, contactFunctions)}
            options={activeThirdPartyContactsFunctionsOptions(contactFunctions)}
            isClearable={true}
            isMulti={true}
            isRequired
            label={'Function(s)'}
          />
        )}
      </SpruceField>
      <SpruceField name={'emailAddress'}>
        {({ field, formik }: ISpruceFieldProps) => (
          <TextInput
            {...field}
            onBlur={() => formik.setFieldTouched(field.name)}
            label={'Email'}
            readOnly={disableEmail}
            isRequired
          />
        )}
      </SpruceField>
      <SpruceField name={'companyName'}>
        {({ field }: ISpruceFieldProps) => <TextInput {...field} label={'Company Name'} isRequired />}
      </SpruceField>
      <SpruceField name={'phoneNumber'}>
        {({ field }: ISpruceFieldProps) => <PhoneNumberInput {...field} label={'Phone Number'} />}
      </SpruceField>
      {showAddressFields && (
        <>
          <Grid templateColumns={'3fr 1fr'}>
            <SpruceField name="mailingAddress.streetAddress">
              {({ field }: ISpruceFieldProps) => (
                <TextInput
                  {...field}
                  data-test-id={field.name}
                  label={`Street Address`}
                  isRequired={true}
                  placeholder={'123 Main Street'}
                />
              )}
            </SpruceField>
            <SpruceField name="mailingAddress.unit">
              {({ field }: ISpruceFieldProps) => (
                <TextInput {...field} data-test-id={field.name} label={`Unit`} isRequired={false} placeholder={'4'} />
              )}
            </SpruceField>
          </Grid>
          <SpruceField name="mailingAddress.city">
            {({ field }: ISpruceFieldProps) => (
              <TextInput
                {...field}
                data-test-id={field.name}
                label={`City`}
                isRequired={true}
                placeholder={'Manhattan'}
              />
            )}
          </SpruceField>
          <Grid templateColumns={'3fr 1fr'}>
            <SpruceField name={'mailingAddress.stateAbbreviation'} inputType={'select'}>
              {({ field }: ISpruceFieldProps) => (
                <Select
                  {...field}
                  filterOption={stateFilterOptions}
                  value={StatesLabelValue.find((state) => state.value === field.value) || null}
                  options={StatesLabelValue}
                  isClearable={true}
                  label={'State'}
                  isRequired
                  placeholder={'Select a State'}
                />
              )}
            </SpruceField>
            <SpruceField name="mailingAddress.zipCode">
              {({ field, formik }: ISpruceFieldProps) => (
                <FormCell errorText={field.errorText} label={`Zip Code`} isRequired>
                  <NumberInputStyled
                    errorText={field.errorText}
                    allowEmptyFormatting={false}
                    value={field.value || undefined}
                    format={`#####`}
                    type={'tel'}
                    placeholder={`12345`}
                    onBlur={() => formik.setFieldTouched(field.name)}
                    onValueChange={({ value, ...rest }: NumberFormatValues) => {
                      formik.setFieldValue(field.name, value ? value.trim() : value);
                    }}
                  />
                </FormCell>
              )}
            </SpruceField>
          </Grid>
        </>
      )}
      <OrderContactNotesField name={'notes'} />
    </>
  );
};
